import { Injectable } from "@angular/core";
import { AlertController, LoadingController, ModalController } from "@ionic/angular";
import { ObserverService } from "./observer.service";
import { APIService } from "./api.service";
import { StorageService } from "./storage.service";
import { loadStripeTerminal, Reader, Terminal } from "@stripe/terminal-js";
import { SettingsChangeStripeTerminalReaderPage } from "../modals/settings-change-stripe-terminal-reader/settings-change-stripe-terminal-reader.page";
import { Decimal } from "decimal.js";

type ConnectionStatus = "not_connected" | "connecting" | "connected";
type PaymentStatus = "not_ready" | "ready" | "waiting_for_input" | "processing";
@Injectable({
	providedIn: "root",
})
export class StripeTerminalReaderService {
	initialised: null | Promise<void>;
	terminal: Terminal;
	cart: any = { items: [], tax_price: 0, amount_due: 0, payments: [], currency : { code : "usd" } }; //Currency will be overwritten by cart or order_add_payment
	payment_intent = null;
	connection_status: ConnectionStatus = "not_connected";
	payment_status: PaymentStatus = "not_ready";
	current_reader: Reader | null = null;
    spinner = null;

	constructor(
		public alertController: AlertController,
		private observerService: ObserverService,
		private apiService: APIService,
		private storageService: StorageService,
		private modalController: ModalController,
		private loadingController: LoadingController
	) {
		this.initialised = this.init();
	}

	async init() {
		const StripeTerminal = await loadStripeTerminal();
		const terminal = StripeTerminal.create({
			onFetchConnectionToken: () => this.getConnectionToken(),
			onConnectionStatusChange: async ({ status: connection_status }: { status: ConnectionStatus }) => {
				this.connection_status = connection_status;
				const reader = await this.getConnectedReader();
				this.current_reader = reader;
				this.observerService.publish("stripe_terminal_reader_changed", reader);
			},
			onPaymentStatusChange: async ({ status }) => (this.payment_status = status),
			onUnexpectedReaderDisconnect: () => {}, //Ignore
		});
		this.storageService.get("stripe_terminal_reader").then(reader => {
			if (reader) {
				this.connectReader(reader);
			}
		});
		this.observerService.subscribe("cart_changed|stripe_terminal_reader_service", new_cart => {
            if(this.connection_status !== "connected") return;
            const items = new_cart.items.map(({ quantity, sell_price, title }) => ({ description: title, quantity, amount: Math.round(sell_price * 100) }));
			var price_subtractions = new_cart.payments.map(({ price, label }) => ({
				amount: Number(new Decimal(price).times(-100).toDecimalPlaces(0)),
				description: `Payment (${label})`,
				quantity: 1,
			})); //Discounts, payments
			if (new_cart.discount_price > 0) {
                //Discount, so * -100
				price_subtractions.splice(0, 0, { description: "Discount", amount: Number(new Decimal(new_cart.discount_price).times(-100).toDecimalPlaces(0)), quantity: 1 });
			}
			if(new_cart.shipping) {
				price_subtractions.splice(0, 0, { description: "Shipping", amount: Number(new Decimal(new_cart.shipping.price).times(100).toDecimalPlaces(0)), quantity: 1 });
			}
            if (new_cart.payment_surcharge.price > 0) {
                price_subtractions.splice(0, 0, { description: "Payment surcharge", amount: Number(new Decimal(new_cart.payment_surcharge.price).times(100).toDecimalPlaces(0)), quantity: 1 });
            }
			this.cart = {
				...new_cart,
				items: [...items, ...price_subtractions],
			};
			if (this.connection_status === "connected") {
				this.refreshCart();
			}
		});
        this.observerService.subscribe("stripe_terminal_payment_closed", () => {
            if(this.connection_status !== "connected") return;
            this.terminal.clearReaderDisplay();
        });
		this.observerService.subscribe("stripe_terminal_add_payment_price_changed", ({ payment_price, surcharge_price, currency }) => {
            if(this.connection_status !== "connected") return;
			if (payment_price) {
                this.cart = {
                    items: [{ description: "Payment", quantity: 1, amount: Number(new Decimal(payment_price).times(100).toDecimalPlaces(0)) }],
                    amount_due: payment_price,
                    currency,
                };
                if(surcharge_price) {
                    const surcharge_price_cents = Number(new Decimal(surcharge_price).times(100).toDecimalPlaces(0));
                    this.cart.items[0].amount = Number(new Decimal(this.cart.items[0].amount).minus(surcharge_price_cents));
                    this.cart.items.push({ 
                        description: "Payment surcharge", 
                        quantity: 1, 
                        amount: surcharge_price_cents
                    });
                }
			} else {
				this.cart = {
					items: [],
					amount_due: 0,
					currency,
				};
			}
			if (this.connection_status === "connected") {
				this.refreshCart();
			}
		});
		this.terminal = terminal;
	}

	async refreshCart() {
		this.terminal.setReaderDisplay({
			type: "cart",
			cart: {
				line_items: this.cart.items.filter(item => item.quantity > 0),
				tax: this.cart.pricing_tier?.tax_inclusive ? undefined : Number(new Decimal(this.cart.tax_price).times(100).toDecimalPlaces(0)), //Only show tax if tax_inclusive == false
				total: Number(new Decimal(this.cart.amount_due).times(100).toDecimalPlaces(0)),
				currency: this.cart.currency.code,
			},
		});
	}

	async cancelCharge() {
		this.cancelStripePaymentIntent();
		const res = await this.terminal.cancelCollectPaymentMethod();
		this.refreshCart();
		return res;
	}

	async charge(amount, currency): Promise<{ error: any } | { paymentIntent: any }> {
		const available = await this.checkAvailable();
		if (!available) return { error: { code: "reader_error" } };
		const payment_intent = await this.createStripePaymentIntent(amount, currency);
		this.payment_intent = payment_intent;
		return this.terminal.collectPaymentMethod(payment_intent["client_secret"]);
	}

	async processPayment(payment_intent): Promise<{ error: any } | { paymentIntent: any }> {
		return this.terminal.processPayment(payment_intent);
	}

	async getConnectedReader() {
		await this.initialised;
		return this.terminal.getConnectedReader();
	}

	async checkAvailable(): Promise<Boolean> {
		return new Promise(async (resolve, reject) => {
			if (this.connection_status === "not_connected") {
				resolve(this.readerNotAvailable());
			} else if (this.connection_status === "connecting") {
				const loading = this.loadingController.create({ message: "Connecting to reader" });
				loading.then(loaded => loaded.present());
                let status_changed = false;
				this.observerService.subscribe("stripe_terminal_reader_changed", async reader => {
                    if(!status_changed){
                        status_changed = true;
                        loading.then(loaded => loaded.dismiss());
                        if (reader === null) {
                            resolve(this.readerNotAvailable(true));
                        } else {
                            resolve(true);
                        }
                    }
				});
			} else {
				//connected
				resolve(true);
			}
		});
	}

	private async readerNotAvailable(skip_connect_attempt = false): Promise<Boolean> {
		return new Promise<Boolean>(async (resolve, reject) => {
			const reader = await this.storageService.get("stripe_terminal_reader");
			const buttons = [
				{
					text: "Manage readers",
					handler: () => resolve(this.manageReaders()),
				},
				{
					text: "OK",
					handler: () => resolve(false),
				},
			];
			if (reader) {
				if (!skip_connect_attempt) {
					const connection_result = await this.connectReaderWithLoading(reader);
					if (connection_result === "connected") {
						resolve(true);
						return;
					}
				}
				buttons.splice(1, 0, {
					//Insert index 1 so middle of array, buttons sorted by length of text
					text: `Retry ${reader.label}`,
					handler: () => resolve(this.readerNotAvailable()),
				});
			}
			const alert = await this.alertController.create({
				header: "Problem",
				subHeader: "Not connected to reader",
				message: reader ? "Failed to connect to reader " + reader["label"] : undefined,
				buttons,
			});
			await alert.present();
		});
	}

	async connectReaderWithLoading(reader) {
		const loading = await this.loadingController.create({ message: `Connecting to ${reader.label}` });
		loading.present();
		const connection_result = await this.connectReader(reader);
		await loading.dismiss();
		return connection_result;
	}

	manageReaders(): Promise<Boolean> {
		return new Promise<Boolean>((resolve, reject) => {
			this.modalController
				.create({
					component: SettingsChangeStripeTerminalReaderPage,
					cssClass: "settings-change-stripe-terminal-reader",
				})
				.then(modal => {
					modal.onDidDismiss().then(data => resolve(data.data === "connected"));
					modal.present();
				});
		});
	}

	async connectReader(reader): Promise<"connected" | "not_connected"> {
		await this.initialised;
		const res = await this.terminal.connectReader(reader);
		if ("reader" in res) {
			this.storageService.set("stripe_terminal_reader", res["reader"]);
			// this.observerService.publish("stripe_terminal_reader_changed", res["reader"]);
			this.refreshCart();
			return "connected";
		} else {
			this.observerService.publish("stripe_terminal_reader_changed", null);
			return "not_connected";
		}
	}

	async disconnectReader() {
		await this.initialised;
		// this.forgetReader();
		// this.observerService.publish("stripe_terminal_reader_changed", null);
		return this.terminal.disconnectReader();
	}

	forgetReader() {
		this.observerService.publish("stripe_terminal_reader_changed", null);
		this.observerService.publish("stripe_terminal_reader_forgotten", true);
		this.storageService.set("stripe_terminal_reader", null);
	}

    getConnectionToken(): Promise<string> {
		return new Promise((resolve, reject) => {
			this.apiService
				.createStripeTerminalConnectionToken()
				.then(token => {
					if ("secret" in token) {
						resolve(token.secret);
					} else {
						this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when fetching a connection token from Stripe.");
						reject();
					}
				})
				.catch(({ status }) => {
					if (status === 403) {
						this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to fetch a connection token.");
					} else if (status === 400) {
						this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when fetching a connection token from Stripe.");
					}
					reject();
				});
		});
	}

	cancelStripePaymentIntent() {
		return new Promise((resolve, reject) => {
			this.apiService
				.cancelStripePaymentIntent(this.payment_intent["id"])
				.then(resolve)
				.catch(({ status }) => {
					if (status === 403) {
						this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to cancel a payment intent");
					} else if (status === 400) {
						this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when cancelling a payment intent");
					}
					reject();
				});
		});
	}

	createStripePaymentIntent(amount, currency) {
		return new Promise((resolve, reject) => {
			this.apiService
				.createStripePaymentIntent(Number(new Decimal(amount).times(100).toDecimalPlaces(0)), currency)
				.then(resolve)
				.catch(({ status }) => {
					if (status === 403) {
						this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to create a payment intent.");
					} else if (status === 400) {
						this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when creating a payment intent.");
					}
					reject();
				});
		});
	}

	refundStripePaymentIntent(payment_intent_id) {
		return new Promise((resolve, reject) => {
			this.apiService
				.refundStripePaymentIntent(payment_intent_id)
				.then(resolve)
				.catch(({ status }) => {
					if (status === 403) {
						this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to refund this payment intent.");
					} else if (status === 400) {
						this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to refund a payment intent.");
					}
					reject();
				});
		});
	}

	loadReaders() {
		return new Promise<any>((resolve, reject) => {
			this.apiService
				.getStripeTerminalReaders()
				.then(res=>resolve({readers:res}))
				.catch(({ status, error }) => {
					if (status === 403) {
						this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to view readers.");
					} else if (status === 400) {
						if (error.errors.length === 1 && error.errors[0] === "stripe_not_connected") {
                            resolve({error:'stripe_not_connected'});
                        } else {
							this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to get readers.");
						}
					}
					reject();
				});
		});
	}

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

	async presentAlert(header, sub_header, message) {
		const alert = await this.alertController.create({
			header: header,
			subHeader: sub_header,
			message: message,
			buttons: ["OK"],
		});
		await alert.present();
	}
}
